import {
  KEYS,
  TOPIC_CHIP_ALL,
  WRAP_CHIPS_MAX_TOPIC_COUNT,
  TempElemAttrSuffix,
  CitationLoaderId,
  AttrDataTempElem,
  AttrDataRefLoader,
  ONBOARDING,
  ONE_MB,
  StorageLevelEnum,
  CitationLoaderAnimationClass,
  Events,
  MenuWordLimit,
  InlineSuggestionId,
} from 'src/researcher/constants/common.constants';
import QuillEditor, { QuillEditorIDS } from 'src/_common/handlers/quillEditor';
import { Months } from 'src/citation-generator/constants';
import { MANIFOLD_TAB_SEQ } from 'src/researcher/types/control';
import logger from 'core/logger/logger';
import PDFJS from 'pdfjs-dist/es5/build/pdf';
import PDFJSWorker from 'pdfjs-dist/es5/build/pdf.worker.js';
import mammoth from 'mammoth';
import { GenericObject } from 'core/models/platform';
import { generateRandomId, safeJSONParse, safeJSONStringify } from 'core/utils';
import { CitationData, CitedData, CitationStylesEnum } from 'src/citation-generator/types';
import { createCitationTooltipHtml } from 'src/researcher/component/common';
import { getWordCount } from '@Common/handlers/helper';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { EditorExecCommands } from '../models/IBaseEditorTypes';
import { ArticleOutline, ResultCardState } from '../types/research';
import { BookmarkCardState } from '../types/bookmark';
import { TopicAndContents } from '../models';
import { updateCitationMarkerForThisCitation } from '../component/citationTab/utils';

PDFJS.disableTextLayer = true;
PDFJS.disableWorker = true; // not availaible anymore since 2.3.0 (see imports)

interface GetTextFromFileOptions {
  textOutputFormat?: 'html' | 'raw';
  allowImages?: boolean;
}
export interface PageContent {
  images: string[];
  pageText: string;
}

export enum WhichPara {
  CURRENT = 'current',
  NEXT = 'next',
  PREVIOUS = 'previous',
}

export enum SupportedFileFormat {
  PDF = 'application/pdf',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  DOCX_SHORT = 'docx',
  DOC = 'application/msword',
  DOC_SHORT = 'doc',
  TXT = 'text/plain',
  TXT_SHORT = 'txt',
}

/**
 * Make a new object from `obj` with all keys that are in `keys`
 */
export function pick(obj: GenericObject, keys: string[]) {
  const newObj: GenericObject = {};
  keys.forEach((f) => {
    newObj[f] = obj[f];
  });
  return newObj;
}

/**
 * Get citation marker node for editor
 */
export function getCitationRefNode(metaInfo: CitationData | null, citedData: CitedData) {
  if (!metaInfo?.publicationInfo?.title) return '';

  return createCitationTooltipHtml(metaInfo, citedData);
}

/**
 * Append text with given style to editor dom
 */
export function appendToEditorDOM(
  style: string,
  text: string,
  citationNode?: string,
  withQuotes = true,
  appendToNewLine = true
) {
  const nodeId = generateRandomId();
  const trimmedText = text.trim();
  const textFirstCharacter = trimmedText[0];
  const textLastCharacter = trimmedText[trimmedText.length - 1];
  let textToInsert = trimmedText;
  if (textFirstCharacter !== '"' && withQuotes) {
    textToInsert = `"${textToInsert}`;
  }
  if (textLastCharacter !== '"' && withQuotes) {
    textToInsert = `${textToInsert}"`;
  }

  const line = [
    `<span id="${nodeId}" class="showEffect ${
      ONBOARDING.CLASSES.INSERTED_PARAGRAPH
    }" style="${style}">${textToInsert.trim()}</span>`,
  ];

  if (citationNode?.length) {
    line.push('<span>&#x00A0;</span>');
    line.push(citationNode);
    line.push('<span>&#x00A0;</span>');
  }

  if (appendToNewLine) {
    QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)?.appendHTMLAtNewLine(line.join(''));
  } else {
    QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)?.appendHTML(line.join(''));
  }

  const spanAddedNow = document.getElementById(`${nodeId}`);
  spanAddedNow?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  setTimeout(() => {
    spanAddedNow?.removeAttribute('id');
    spanAddedNow?.classList.remove('showEffect');
  }, 2000);
}

export function appendToEditorDOMWithCallback(
  style: string,
  text: string,
  withQuotes = true,
  appendToNewLine = true
) {
  const nodeId = generateRandomId();
  const loaderId = `${nodeId}${TempElemAttrSuffix}`;
  const trimmedText = text.trim();
  const textFirstCharacter = trimmedText[0];
  const textLastCharacter = trimmedText[trimmedText.length - 1];
  let textToInsert = trimmedText;
  if (textFirstCharacter !== '"' && withQuotes) {
    textToInsert = `"${textToInsert}`;
  }
  if (textLastCharacter !== '"' && withQuotes) {
    textToInsert = `${textToInsert}"`;
  }

  const contentHTML = [
    `<span id="${nodeId}" class="showFadeEffect ${
      ONBOARDING.CLASSES.INSERTED_PARAGRAPH
    }" style="${style}">${textToInsert.trim()}</span>`,
    '<span>&#x00A0;</span>',
    `<span id="${loaderId}" ${AttrDataTempElem}="${loaderId}" contenteditable="false"><span id=${CitationLoaderId} class="${CitationLoaderAnimationClass}"></span></span>`,
    '<span>&#x00A0;</span>',
  ].join('');

  if (appendToNewLine) {
    QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)?.appendHTMLAtNewLine(contentHTML);
  } else {
    QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)?.appendHTML(contentHTML);
  }

  const spanAddedNow = document.getElementById(`${nodeId}`);
  spanAddedNow?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  return (content: string) => {
    QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)?.replaceElement(loaderId, content);
    const asyncElement = document.getElementById(loaderId);
    if (asyncElement) {
      asyncElement.removeAttribute(AttrDataTempElem);
      asyncElement.removeAttribute('id');
    }

    if (spanAddedNow) {
      spanAddedNow.removeAttribute('id');
      spanAddedNow.classList.remove('showFadeEffect');
    }
  };
}

export function setupTempLoadersForCitations(citations: CitationData[]) {
  const selector = citations.map((citation) => `[data-ref='${citation.refId}']`).join(', ');
  const nodes = selector ? document.querySelectorAll(selector) : [];
  nodes.forEach((node) => {
    const ref = node.getAttribute('data-ref') || '';
    // The idea here is to keep data intact even when content is saved while citations are being generated.
    node.classList.add('dataRefHidden');
    node.insertAdjacentHTML(
      'afterend',
      `<span ${AttrDataRefLoader}=${ref} class="pulseLoader"></span>`
    );
  });
}

export async function replaceTempLoadersWithCitedData(citations: CitationData[]) {
  const LocalStorage = QuillLocalStorage.getInstance();
  const citedDataById = <{ [id: string]: CitedData }>(
    safeJSONParse(LocalStorage.get('citedDataById', '{}'), {})
  );

  citations.forEach((citation) => {
    updateCitationMarkerForThisCitation(citation, citedDataById[citation.refId]);
  });

  const selector = citations.map((c) => `[${AttrDataRefLoader}='${c.refId}']`).join(', ');
  const loaderNodes = selector ? document.querySelectorAll(selector) : [];
  loaderNodes.forEach((loader) => {
    // removing loader
    loader.parentElement?.removeChild?.(loader);
  });

  // for co-writer: re-run & re-paint gc errors
  QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)?.fire(Events.RunGcCustomEvent);
}

async function getTextFromPdfForPageNo(
  pdf: Pdf,
  pageNo: number,
  options?: GetTextFromFileOptions
): Promise<PageContent> {
  const response: PageContent = {
    images: [],
    pageText: '',
  };

  const page = await pdf.getPage(pageNo);
  // image operations
  if (options?.allowImages) {
    try {
      const opList: OperatorList = await page.getOperatorList();
      const svgGfx = new PDFJS.SVGGraphics(page.commonObjs, page.objs);
      svgGfx.embedFonts = true;
      opList.fnArray.forEach((ele, i) => {
        if (ele === PDFJS.OPS.paintJpegXObject || ele === PDFJS.OPS.paintImageXObject) {
          const img = page.objs.get(opList.argsArray[i][0]);
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          response.images.push(dataURL);
        }
      });
    } catch (e) {
      logger('Error while extracting images', e);
    }
  }
  // text extraction
  const tokenizedText = await page.getTextContent({
    normalizeWhitespace: false,
    disableCombineTextItems: false,
  });
  let lastY = tokenizedText.items[0].transform[5];
  let minSameParaLineDifference = Number.MAX_VALUE;

  tokenizedText.items.forEach((token) => {
    if (lastY !== token.transform[5] && minSameParaLineDifference > lastY - token.transform[5]) {
      minSameParaLineDifference = lastY - token.transform[5];
    }
  });
  let pageText = '';
  tokenizedText.items.forEach((token) => {
    if (lastY !== token.transform[5]) {
      if (lastY - token.transform[5] > minSameParaLineDifference) {
        pageText += '\n';
      } else {
        pageText += ' ';
      }
      lastY = token.transform[5];
    }
    pageText += `${token.str}`;
  });
  response.pageText = pageText;
  return response;
}

export async function getTextFromPdf(
  source: PDFSource,
  options?: GetTextFromFileOptions
): Promise<PageContent[]> {
  Object.assign(window, { pdfjsWorker: PDFJSWorker }); // added to fit 2.3.0
  const pdf: Pdf = await PDFJS.getDocument({
    data: source,
    nativeImageDecoderSupport: PDFJS.NativeImageDecoding.DISPLAY,
  }).promise;
  const maxPages = pdf.numPages;
  const pageTextPromises = [];
  for (let pageNo = 1; pageNo <= maxPages; pageNo += 1) {
    pageTextPromises.push(getTextFromPdfForPageNo(pdf, pageNo, options));
  }
  const pageTexts = await Promise.all(pageTextPromises);
  return pageTexts;
}

export async function getTextFromDocx(
  file: ArrayBuffer,
  options?: GetTextFromFileOptions
): Promise<PageContent[]> {
  if (options?.textOutputFormat === 'html') {
    return mammoth
      .convertToHtml({ arrayBuffer: file })
      .then((result: { value: string }) => [{ images: [], pageText: result.value }]);
  }

  return mammoth
    .extractRawText({ arrayBuffer: file })
    .then((resultObject: { value: string }) => [{ images: [], pageText: resultObject.value }]);
}

export function getTextFromFile(
  file: PDFSource | ArrayBuffer | string,
  type: SupportedFileFormat,
  options?: GetTextFromFileOptions
): Promise<PageContent[]> {
  if (type === SupportedFileFormat.PDF) {
    return getTextFromPdf(file, options);
  }
  if (type === SupportedFileFormat.DOCX) {
    return getTextFromDocx(file as ArrayBuffer, options);
  }
  if (type === SupportedFileFormat.DOCX_SHORT) {
    return getTextFromDocx(file as ArrayBuffer, options);
  }
  if (type === SupportedFileFormat.DOC) {
    return Promise.resolve([{ pageText: file as string, images: [] }]);
  }
  if (type === SupportedFileFormat.DOC_SHORT) {
    return Promise.resolve([{ pageText: file as string, images: [] }]);
  }
  if (type === SupportedFileFormat.TXT) {
    return Promise.resolve([{ pageText: file as string, images: [] }]);
  }
  if (type === SupportedFileFormat.TXT_SHORT) {
    return Promise.resolve([{ pageText: file as string, images: [] }]);
  }
  return Promise.reject(new Error(`No suppot for ${type} file`));
}

/**
 * Validations
 */

export function isString(value: any) {
  return typeof value === 'string';
}

export function isNumber(value: any) {
  const v = Number(value);
  return typeof v === 'number' && !Number.isNaN(v);
}

export function isLengthLessThen(value: string, lessThen: number) {
  const v = value.trim();
  return v.length <= lessThen;
}

export function isLengthGreaterThen(value: string, greaterThen: number) {
  const v = value.trim();
  return v.length >= greaterThen;
}

export function isLengthBetween(value: string, min: number, max: number) {
  const v = value.trim();
  return isLengthGreaterThen(v, min) && isLengthLessThen(v, max);
}

export function decodeURL(uri: string) {
  try {
    return { error: null, data: decodeURIComponent(uri) };
  } catch (error) {
    return { error, data: decodeURIComponent(uri) };
  }
}

export function isValidMongoID(id: string) {
  const validMongoIdRegex = new RegExp('^[0-9a-fA-F]{24}$');
  return validMongoIdRegex.test(id);
}

/**
 * # Text transform and operations
 */

/**
 * Replace space, new lines, tabs with single space
 */
export const replaceSpaces = (str: string): string => str?.replace?.(/\s\s+/g, ' ');
/** Replace multiple Space with single */
export const replaceOnlySpaces = (str: string): string => str?.replace?.(/  +/g, ' ');
/** Truncate string if `str.length` > `noOfChars` truncate and append `charsToAppend = '...'` */
export function truncateStringAfter(str: string, noOfChars = 50, charsToAppend = '...') {
  return str.length > noOfChars ? `${str.substr(0, noOfChars).trim()}${charsToAppend}` : str;
}

/**
 * # Utilities
 */

/**
 * get list of months
 */
export function getListOfMonths(shortenMonth?: boolean) {
  if (shortenMonth) {
    return Months.map((monthFullName) => monthFullName.substr(0, 3));
  }
  return Months;
}
/** Get name of month */
export function getMonth(month: number) {
  return getListOfMonths()[month];
}
/** check if given string is valid date, return date if valid else null  */
export function parseDateFromString(dateString: string) {
  if (Date.parse(dateString)) {
    return new Date(dateString);
  }
  return null;
}
/** Cal x as percentage of y */
export const percentage = (x: number, y: number) => (x / y) * 100;
/** from toolpane tab order pick tab at index */
export const getManifoldTabLabelForThisTabIndex = (tabIndex: number) => MANIFOLD_TAB_SEQ[tabIndex];
/** from toolpane tab order pick get tab index for tab name */
export const getManifoldTabIndexForThisLabel = (label: string) =>
  MANIFOLD_TAB_SEQ.findIndex((tab) => tab === label);

export const getContentFromEditor = (editorId: QuillEditorIDS, isPlainTextRequired?: boolean) =>
  QuillEditor.getInstance(editorId)?.getContent(isPlainTextRequired ? 'text' : 'html') || '';

export const getContentFromEditorByRemovingPageWrapper = (editorId: QuillEditorIDS) => {
  const rootDiv = document.createElement('div');
  const body = QuillEditor.getInstance(editorId)?.getBody();
  if (body) {
    const pages = body.querySelectorAll('.page-wrapper');
    for (let i = 0; i < pages.length; i += 1) {
      const page = pages[i];
      rootDiv.innerHTML += page.innerHTML;
    }
  }
  return rootDiv;
};

/** get storage quota for user */
export const getStorageQuotaForUser = (premium: boolean) => (premium ? 20 * ONE_MB : 2 * ONE_MB);
/** get storage level for given percent of used storage */
export const getStorageLevel = (percent: number): StorageLevelEnum => {
  if (percent < 80) {
    return StorageLevelEnum.NORMAL;
  }
  if (percent >= 80 && percent < 90) {
    return StorageLevelEnum.INFO;
  }
  if (percent >= 90 && percent < 95) {
    return StorageLevelEnum.WARNING;
  }
  if (percent >= 95 && percent < 99) {
    return StorageLevelEnum.DANGER;
  }
  return StorageLevelEnum.CRITICAL;
};
/** format bytes in kb, mb */
export const storageFormatter = (bytes: number, precesion = 2): string => {
  const kb = bytes / 1024;
  const mb = kb / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(precesion).replace(/[.,]00$/, '')} KB`;
  }
  return `${mb.toFixed(precesion).replace(/[.,]00$/, '')} MB`;
};
export const escapeRegex = (topic: string) => topic.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
/** convert full-url into short url */
export function getShortUrl(url: string) {
  return url
    .slice(url.indexOf('//') + 2)
    .split('/')[0]
    .replace(/^www./, '');
}
/** decode html entities from html */
export const decodeHTMLEntities = (html: string) => {
  const temporaryDivElement = document.createElement('textarea');
  temporaryDivElement.innerHTML = html;
  return temporaryDivElement.value || '';
};
/** get text from html */
export const getTextFromHTMLString = (html: string) => {
  const temporaryDivElement = document.createElement('div');
  temporaryDivElement.innerHTML = html;
  return temporaryDivElement.textContent || temporaryDivElement.innerText || '';
};
/** display date string in user friendly time format `Yesterday|Today HH:MM` or `Day(Mon, Tue...) Month(June, July...) Year(2020) HH:MM`  */
export function getDisplayDateAndTime(dateString: string) {
  const today = new Date();
  const compDate = new Date(dateString);
  const hourMinuteTime = compDate.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
  const day = compDate.getDate();
  const year = compDate.getFullYear();
  const month = compDate.getMonth();
  if (year === today.getFullYear() && month === today.getMonth()) {
    if (day === today.getDate()) {
      return `Today ${hourMinuteTime}`;
    }
    if (day + 1 === today.getDate()) {
      return `Yesterday ${hourMinuteTime}`;
    }
  }

  const formattedDate = `${getListOfMonths(true)[month]} ${day}, ${hourMinuteTime}`;
  return formattedDate;
}

const pasteData = (
  data: { types: string[]; getType: (type: string) => Promise<Blob> }[],
  type: 'text/html' | 'text/plain',
  callback?: Function
) => {
  data[0]
    .getType(type)
    .then((blob) => {
      blob
        .text()
        .then((html: string) => {
          QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)?.execCommand(
            EditorExecCommands.MCEINSERTCLIPBOARDCONTENT,
            false,
            {
              content: html,
            }
          );
          callback?.();
        })
        .catch((err) => {
          console.log(err);
        });
    })
    .catch((err) => {
      console.log(err);
    });
};

export const handleContextMenuAndShortCutPaste = (
  withoutFormatting: boolean,
  callback?: Function
) => {
  (navigator?.clipboard as any)
    ?.read()
    .then((data: { types: string[]; getType: (type: string) => Promise<Blob> }[]) => {
      if (!withoutFormatting) {
        if (data[0].types.includes('text/html')) {
          pasteData(data, 'text/html');
        } else if (data[0].types.includes('text/plain')) {
          pasteData(data, 'text/plain');
        }
      } else if (withoutFormatting && data[0].types.includes('text/plain')) {
        pasteData(data, 'text/plain');
      }
    })
    .catch((err: any) => {
      console.log(err);
    });
};

export const insertNeccessaryAttributesToTopLevelNodes = (content: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content || '';
  let paragraphid = 1;
  if (tempDiv.childNodes.length === 1 && tempDiv.childNodes[0].nodeType === 3) {
    const pNode = document.createElement('p');
    (pNode as HTMLElement).setAttribute('class', 'root-block-pNode');
    (pNode as HTMLElement).setAttribute('data-paragraphid', (paragraphid += 1).toString());
    (pNode as HTMLElement).setAttribute('data-from-init', 'true');
    (pNode as HTMLElement).setAttribute('data-changed', 'true');
    pNode.innerHTML = tempDiv.innerHTML;
    const divForInnerHTML = document.createElement('div');
    divForInnerHTML.appendChild(pNode);
    tempDiv.innerHTML = divForInnerHTML.innerHTML;
    return { transFormedContent: tempDiv.innerHTML, paragraphid };
  }

  tempDiv.childNodes.forEach((node) => {
    if (
      // text nodes are also created, to skip them we are checking for dataset as,
      // for text nodes there is no dataset property.
      (node as HTMLElement).dataset
    ) {
      (node as HTMLElement).setAttribute('class', 'root-block-node');
      (node as HTMLElement).setAttribute('data-paragraphid', (paragraphid += 1).toString());
      (node as HTMLElement).setAttribute('data-from-init', 'true');
      (node as HTMLElement).setAttribute('data-changed', 'true');
    }
  });
  return { transFormedContent: tempDiv.innerHTML, paragraphid };
};

export const replaceElement = (
  id: string,
  rawHTML: string,
  ignoreUndo?: boolean,
  retainCursorPos?: boolean
): void => {
  const unsafeEditorInstance = QuillEditor.getInstance(
    QuillEditorIDS.RESEARCH_EDITOR
  )?.dangerouslyGetEditorLibInstance();
  if (ignoreUndo) {
    unsafeEditorInstance?.undoManager.ignore(() => {
      unsafeEditorInstance?.execCommand('mceSelectNode', true, unsafeEditorInstance?.dom.get(id));
      unsafeEditorInstance?.execCommand('mceReplaceContent', true, rawHTML);
    });
  } else {
    unsafeEditorInstance?.execCommand('mceSelectNode', true, unsafeEditorInstance?.dom.get(id));
    unsafeEditorInstance?.execCommand('mceReplaceContent', true, rawHTML);
  }

  if (retainCursorPos) {
    unsafeEditorInstance?.selection.setCursorLocation(unsafeEditorInstance?.dom.get(id) as Node, 0);
  }
};

export const createInlineSuggestionHTML = (text: string) =>
  `<span id="inlineSuggestion" style="color: gray;" class="inlineSuggestion">${text}</span>`;

export const removeAllInlineSuggestions = () =>
  document.querySelectorAll('#inlineSuggestion').forEach((ele) => ele.remove());

export const clearSelection = () => {
  if (window?.getSelection) {
    window?.getSelection()?.removeAllRanges();
  } else if (document.selection) {
    (document.selection as any).empty();
  }
};

// get selection node
export function getSelectedNode() {
  if (document.selection) return document.selection.createRange().parentElement();
  const selection = window.getSelection();
  if (!selection || selection.rangeCount <= 0) return null;
  return selection.getRangeAt(0).startContainer.parentNode;
}

export function isSelectionInside(nodeId: string) {
  let out = false;
  const element = document.getElementById(nodeId);
  if (element) {
    const selectionNode = getSelectedNode();
    out = element.contains(selectionNode);
  }
  return out;
}

export const clearSelectionIn = (nodeId: string) => {
  const isSelectionInsideElement = isSelectionInside(nodeId);
  if (isSelectionInsideElement) {
    clearSelection();
  }
};

export const restoreSelection = (range: Range) => {
  if (range) {
    if (window.getSelection) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } else if (document.selection && (range as any).select) {
      (range as any).select();
    }
  }
};

export const saveSelection = () => {
  if (window.getSelection) {
    const sel = window.getSelection();
    if (sel?.getRangeAt && sel?.rangeCount) {
      return sel?.getRangeAt(0);
    }
  } else if (document.selection && document.selection.createRange) {
    return document.selection.createRange();
  }
  return null;
};

export const calculateUndoTimerReduction = (
  undoTimeInMilliSeconds: number,
  timeChunkInMilliSecondsToDivideIn: number
) => {
  // Calculating reduction in undo time.
  const reductionInUndoTimePerTimeChunk =
    (timeChunkInMilliSecondsToDivideIn * 100) / undoTimeInMilliSeconds;
  return reductionInUndoTimePerTimeChunk;
};

export const removeAllDOMNodesWithClass = (className: string) => {
  Array.from(document.getElementsByClassName(className)).forEach((node) => {
    node.remove();
  });
};
/** Create a Array of `Item`. If `fillFn` is not provided array is list of number from 1 to `length`. */
export const createArray = <Item = number>(
  length: number,
  fillFn?: (item: Item, index: number, arr: Item[]) => Item
) => {
  const arr: Item[] = [];
  for (let i = 0; i < length; i += 1) {
    if (fillFn) {
      arr.push(fillFn(arr[i], i, arr));
    } else {
      arr.push((i + 1) as unknown as Item);
    }
  }
  return arr;
};

export const removeKeysFromLocalStorage = (keysToRemove: string[], keysToExclude?: string[]) => {
  keysToRemove.forEach((key) => {
    if (!keysToExclude?.includes(key)) {
      localStorage.removeItem(key);
    }
  });
};

export const removeNonRestorableKeysFromLS = (
  keysToRemove: string[] = Object.values(KEYS),
  keysToExclude: string[] = [
    KEYS.COWRITER_HAS_PREV_ONBOARDED,
    KEYS.LOCALSTORAGE_NEVER_ASK_DELETE_PROJECT,
    KEYS.LOCALSTORAGE_NEVER_SHOW_DOWNVOTE_DRAWER,
    KEYS.LOCALSTORAGE_DONT_SHOW_STORAGE_BAR,
    KEYS.LOCALSTORAGE_WA_ENCOURAGEMENT_TOOLTIP_COUNT,
    KEYS.LOCALSTORAGE_WA_SHOW_INFORMATION_MODAL,
    KEYS.LOCALSTORAGE_GC_PAINT_RESPONSE,
    KEYS.LOCALSTORAGE_GC_SHOWN_INFORMATION_MODAL,
    KEYS.LOCAL_STORAGE_LIST_GRID_VIEW,
    KEYS.LOCAL_STORAGE_ANIMATION_ONBOARDING,
  ]
) => {
  removeKeysFromLocalStorage(keysToRemove, keysToExclude);
};

export const removeKeysOnLogOut = () => {
  removeKeysFromLocalStorage(Object.values(KEYS));
};

export const formatPublishDate = (
  year?: string | number,
  month?: string | number,
  day?: string | number
) => {
  let date = '';

  if (month) {
    date += `${month}`;
  }

  if (day) {
    date += ` ${day}`;
  }

  if (date.length) {
    date += ',';
  }

  if (year) {
    date += ` ${year}`;
  } else if (date.length) {
    date.replace(',', '');
  }

  return replaceOnlySpaces(date).trim();
};

export const getDisplayPublishDate = (
  citationDate?: CitationData['publicationInfo']['publishDate']
) => {
  let displayDate = '';
  if (citationDate) {
    displayDate = formatPublishDate(citationDate.year, citationDate.month, citationDate.day);
  }
  return displayDate;
};

export const getDisplayAuthorName = (contributors?: CitationData['contributors']) => {
  let displayAuthor = '';
  if (contributors?.length) {
    const authorsAsString = contributors
      .map((c) => [c.firstName, c.middleName, c.lastName].filter(Boolean).join(''))
      .filter(Boolean)
      .join(' ');
    displayAuthor = truncateStringAfter(authorsAsString, 100, '');
  }
  return displayAuthor;
};

export const getCitationDataAttribute = (resource: ResultCardState | BookmarkCardState) => {
  let data = null;
  if (typeof resource === 'object' && resource.id) {
    data = {
      ...resource?.citationData,
      resourceId: resource.id,
      _status_: resource.status,
      _position_: (resource as ResultCardState).position || -1,
    };
  }

  return safeJSONStringify(data || {}, {});
};

export function getTopicsToRender(
  listOfTopics: string[],
  isShowingAllTopics: boolean,
  selectedTopic: string
) {
  // 'All' is first chip always
  let topicsToRender: string[] = listOfTopics;

  if (!isShowingAllTopics) {
    topicsToRender = [];
    // No of chips to show at all time
    // if there is a currently selected topic then include in topics-to-render
    if (selectedTopic && selectedTopic !== TOPIC_CHIP_ALL) {
      topicsToRender.push(selectedTopic);
      // selectedTopic === TOPIC_CHIP_AL
    }
    // as there has to be 4 chips shown...get atleast 4 + 1 next ships from complete list
    const nextTopics = listOfTopics.slice(
      0,
      Math.min(WRAP_CHIPS_MAX_TOPIC_COUNT + 1, listOfTopics.length)
    );
    // for these topics check if it is valid and not already included
    nextTopics?.forEach((topic) => {
      if (topic && !topicsToRender.includes(topic)) {
        topicsToRender.push(topic);
      }
    });
    // make sure there are always max of 4 chips
    topicsToRender.length = Math.min(WRAP_CHIPS_MAX_TOPIC_COUNT, topicsToRender.length);
  }
  return topicsToRender;
}

/**
 * @description Check if source has topics and contents
 */
export function checkHasTopicsAndContents(topics?: TopicAndContents) {
  let ret = false;
  if (topics) {
    ret = Object.keys(topics).length > 0;
  }
  return ret;
}
/**
 *
 * @param articleOutline heading-paragraphs
 * @returns boolean if this outline pair is valid
 */
export function hasArticleOutlineContent(articleOutline: ArticleOutline) {
  return articleOutline.heading && articleOutline.paragraph?.length;
}
/**
 * check has article outline
 */
export function checkHasArticleOutlines(articleOutlines?: ArticleOutline[]) {
  let ret = false;
  if (articleOutlines?.length) {
    ret = articleOutlines?.some?.(hasArticleOutlineContent);
  }
  return ret;
}

export function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

export function once<OnceFunction>(fn: Function): OnceFunction {
  let fire = false;
  return function onceFn(...args: any) {
    if (!fire) {
      fn(...args);
      fire = true;
    }
  } as unknown as OnceFunction;
}

export const hasEnoughText = (
  text = '',
  limit = MenuWordLimit.Default,
  mode: 'word' | 'char' = 'char'
) => {
  const trimText = replaceSpaces(text)?.trim();
  if (mode === 'char') {
    return trimText?.length >= limit;
  }
  return getWordCount(trimText) >= limit;
};

export const getPosition = (
  xy: { x: number; y: number },
  options = { offsetX: 0, offsetY: 0, width: 0, height: 0 }
) => {
  const { x = 0, y = 0 } = xy;
  const { offsetX, offsetY } = options;

  let top = y + offsetY;
  let left = x + offsetX;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  if (top >= viewportHeight) {
    const overflow = top - viewportHeight;
    const safeOffset = Math.floor(overflow * 0.1);
    top = top - overflow + safeOffset;
  }

  if (left >= viewportWidth) {
    const overflow = left - viewportWidth;
    const safeOffset = Math.floor(overflow * 0.1);
    left = left - overflow + safeOffset;
  }

  // now make sure they are not negative as result of above subtraction

  if (top < 0) {
    top += options.width / 2;
  }

  if (left < 0) {
    left += options.height / 2;
  }

  return {
    top: y + offsetY,
    left: x + offsetX,
  };
};

export const hideSuggestionAssistance = () => {
  if (document.getElementById(InlineSuggestionId)) {
    QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)?.replaceElement(
      InlineSuggestionId,
      '',
      true,
      false
    );
  }
};

export const getPara = (whichPara: WhichPara) => {
  // used for pagination
  const paragraphs = document.getElementById(QuillEditorIDS.RESEARCH_EDITOR)?.childNodes;
  if (paragraphs) {
    if (whichPara === WhichPara.PREVIOUS || whichPara === WhichPara.NEXT) {
      QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)
        ?.dangerouslyGetEditorLibInstance()
        .selection.collapse(true);
    }
    const currentlySelectedNode = QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)
      ?.dangerouslyGetEditorLibInstance()
      .selection.getNode() as Node;
    // const currentlySelectedNodeFromWindowAnchorNode = window.getSelection()?.anchorNode as Node
    // const currentlySelectedNodeFromWindowfocusNode = window.getSelection()?.focusNode as Node
    // console.log("currentlySelectedNode", currentlySelectedNode, "currentlySelectedNodeFromWindowAnchorNode", currentlySelectedNodeFromWindowAnchorNode, "currentlySelectedNodeFromWindowfocusNode", currentlySelectedNodeFromWindowfocusNode);
    if (whichPara === WhichPara.PREVIOUS || whichPara === WhichPara.NEXT) {
      for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex += 1) {
        const currentParagraph = paragraphs?.[paragraphIndex];
        if (currentParagraph?.contains(currentlySelectedNode)) {
          QuillEditor.getInstance(QuillEditorIDS.RESEARCH_EDITOR)
            ?.dangerouslyGetEditorLibInstance()
            .selection.select(currentParagraph);
        }
      }
    }

    for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex += 1) {
      const currentParagraph = paragraphs?.[paragraphIndex];
      if (currentParagraph?.contains(currentlySelectedNode)) {
        switch (whichPara) {
          case WhichPara.CURRENT:
            return currentParagraph;
          case WhichPara.PREVIOUS:
            if (paragraphIndex > 0) {
              for (
                let previousParaIndex = paragraphIndex - 1;
                previousParaIndex >= 0;
                previousParaIndex -= 1
              ) {
                if (paragraphs?.[previousParaIndex]?.textContent?.trim()) {
                  return paragraphs?.[previousParaIndex];
                }
              }
            }
            break;
          case WhichPara.NEXT:
            if (paragraphIndex !== paragraphs.length) {
              for (
                let nextParaIndex = paragraphIndex + 1;
                nextParaIndex < paragraphs.length;
                nextParaIndex += 1
              ) {
                if (paragraphs?.[nextParaIndex]?.textContent?.trim()) {
                  return paragraphs?.[nextParaIndex];
                  // return paragraphs?.[];
                }
              }
            }
            break;
        }
      }
    }
  }
};

export const replaceVariablesAndCreateConstantString = (
  text: string,
  variable: { name: string; value: string }
) => {
  const regex = new RegExp(`{{${variable.name}}}`, 'gm');
  return text.replace(regex, `${variable.value}`);
};
